import { PortableText } from '@portabletext/react';

import { ARTICLE_TOKENS } from './design-tokens';
import { buildArticlePortableTextComponents } from './portable-text-components';
import Paywall from '@components/paywall';
import type { PremiumBodyState } from '@hooks/usePremiumBody';
import type { ArticleHeading } from '@lib/article-blocks/headings';
import { PREMIUM_BODY_CLASS_NAME } from '@lib/article-seo';

const PAYWALL_TITLE = 'The rest of this article is for members';
const PAYWALL_SUBTITLE =
  'Pro and Ultra members read every premium article in full.';
const PLACEHOLDER_LINE_WIDTHS = ['w-full', 'w-11/12', 'w-full', 'w-3/4'];

type PremiumBodyProps = {
  state: PremiumBodyState;
  /**
   * Headings from the premium half only, so the ids line up with the table of
   * contents built from the whole article. Copied before use because the
   * component map consumes them as headings render.
   */
  headings: ArticleHeading[];
  returnTo: string;
  /**
   * The contributor's Rewardful code on a team reveal, so a sign-up from the
   * paywall is credited to them as it was on WordPress.
   */
  affiliateCode?: string;
};

const LoadingPlaceholder = () => (
  <div
    className="my-8 space-y-4"
    data-cy="article-premium-loading"
    data-testid="article-premium-loading"
    aria-busy="true"
    aria-label="Loading the rest of this article"
  >
    {PLACEHOLDER_LINE_WIDTHS.map((width, index) => (
      <div
        key={index}
        className={`h-4 animate-pulse rounded bg-neutral-200 ${width}`}
      />
    ))}
  </div>
);

const LoadFailed = ({ retry }: { retry: () => void }) => (
  <div
    className={`my-8 rounded-[14px] border p-6 text-center ${ARTICLE_TOKENS.rule} ${ARTICLE_TOKENS.surfaceTint}`}
    data-cy="article-premium-error"
    role="alert"
  >
    <p className={`m-0 mb-4 ${ARTICLE_TOKENS.body}`}>
      We could not load the rest of this article.
    </p>
    <button
      type="button"
      onClick={retry}
      className="rounded-lg border-2 border-transparent bg-primary-600 px-3 py-[7px] font-open-sans text-base font-bold text-white transition-colors hover:bg-primary-800"
    >
      Try again
    </button>
  </div>
);

/**
 * The gated half of a premium article or team reveal. It sits directly after
 * the free body and shows whichever of the body, a placeholder, the paywall or
 * a retry the visitor's entitlement and the fetch have produced.
 */
export const PremiumBody = ({
  state,
  headings,
  returnTo,
  affiliateCode
}: PremiumBodyProps) => {
  if (state.status === 'not-applicable') return null;

  // The wrapper carries the class the Article structured data names as the
  // paywalled section, whatever state the slot is in.
  return (
    <div className={PREMIUM_BODY_CLASS_NAME}>
      <PremiumBodyContent
        state={state}
        headings={headings}
        returnTo={returnTo}
        affiliateCode={affiliateCode}
      />
    </div>
  );
};

const PremiumBodyContent = ({
  state,
  headings,
  returnTo,
  affiliateCode
}: PremiumBodyProps) => {
  if (state.status === 'ready') {
    return (
      <div className="content-body" data-cy="article-premium-body">
        <PortableText
          value={state.body}
          components={buildArticlePortableTextComponents(state.blockData, [
            ...headings
          ])}
        />
      </div>
    );
  }

  if (state.status === 'error') return <LoadFailed retry={state.retry} />;

  if (state.status === 'locked') {
    return (
      <div className="my-8" data-cy="article-paywall">
        <Paywall
          variant="embedded"
          title={PAYWALL_TITLE}
          subtitle={PAYWALL_SUBTITLE}
          returnTo={returnTo}
          affiliate={affiliateCode}
        />
      </div>
    );
  }

  return <LoadingPlaceholder />;
};
